import { useCallback, useState, type FormEvent, type ReactNode } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface PasswordFormProps {
  readonly onSubmit: (phone: string, password: string) => Promise<void>;
}

export function PasswordForm({ onSubmit }: PasswordFormProps): ReactNode {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);

      if (phone.trim() === "" || password.trim() === "") {
        setError(
          "\u05E0\u05D0 \u05DC\u05DE\u05DC\u05D0 \u05D0\u05EA \u05DB\u05DC \u05D4\u05E9\u05D3\u05D5\u05EA",
        );
        return;
      }

      setIsLoading(true);
      try {
        await onSubmit(phone, password);
      } catch {
        setError(
          "\u05E4\u05E8\u05D8\u05D9 \u05D4\u05EA\u05D7\u05D1\u05E8\u05D5\u05EA \u05E9\u05D2\u05D5\u05D9\u05D9\u05DD",
        );
      } finally {
        setIsLoading(false);
      }
    },
    [phone, password, onSubmit],
  );

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="login-phone" className="text-sm font-medium">
          {"\u05DE\u05E1\u05E4\u05E8 \u05D8\u05DC\u05E4\u05D5\u05DF"}
        </label>
        <Input
          id="login-phone"
          type="tel"
          inputMode="numeric"
          placeholder="05XXXXXXXX"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            setError(null);
          }}
          dir="ltr"
          className="text-start text-base"
          autoComplete="tel"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label htmlFor="login-password" className="text-sm font-medium">
            {"\u05E1\u05D9\u05E1\u05DE\u05D0"}
          </label>
          <button
            type="button"
            className="text-sm text-primary hover:underline cursor-pointer"
          >
            {"\u05E9\u05DB\u05D7\u05EA\u05D9 \u05E1\u05D9\u05E1\u05DE\u05D0"}
          </button>
        </div>
        <div className="relative">
          <Input
            id="login-password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(null);
            }}
            dir="ltr"
            className="text-start text-base pe-11"
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
            aria-label={
              showPassword
                ? "\u05D4\u05E1\u05EA\u05E8 \u05E1\u05D9\u05E1\u05DE\u05D0"
                : "\u05D4\u05E6\u05D2 \u05E1\u05D9\u05E1\u05DE\u05D0"
            }
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5" />
            ) : (
              <Eye className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {error !== null && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading
          ? "\u05DE\u05EA\u05D7\u05D1\u05E8..."
          : "\u05DB\u05E0\u05D9\u05E1\u05D4"}
      </Button>
    </form>
  );
}
